/* Guardian chat widget — scoped to its Shadow DOM. Colours match guardianaccess.com. */
:host {
  --navy: #21435a;
  --slate: #3d5d75;
  --slate-dark: #324c60;
  --ink: #01121f;
  --body: #4a5560;
  --mist: #dde4ea;
  --sand: #efebe9;
  --white: #fff;
  --display: 'Barlow Condensed', 'Oswald', 'Arial Narrow', sans-serif;
  --text: 'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  all: initial;
  font-family: var(--text);
}

*, *::before, *::after { box-sizing: border-box; }
[hidden] { display: none !important; }

.launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2147483000;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 14px 16px;
  border: 0;
  border-radius: 999px;
  background: var(--navy);
  color: var(--white);
  font: 600 15px/1 var(--text);
  box-shadow: 0 10px 30px rgba(1 18 31 / 0.28);
  cursor: pointer;
  transition: transform 180ms ease, background 180ms ease;
}
.launcher:hover { background: var(--slate); transform: translateY(-2px); }
.launcher:focus-visible { outline: 3px solid #7fb0d0; outline-offset: 3px; }
.launcher svg { width: 22px; height: 22px; }
.launcher.is-open span { display: none; }
.launcher.is-open { padding: 14px; }
.launcher.has-news::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  border: 2px solid var(--white);
  border-radius: 50%;
  background: #d93025;
}

.panel {
  position: fixed;
  right: 20px;
  bottom: 88px;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  width: min(380px, calc(100vw - 24px));
  height: min(560px, calc(100vh - 120px));
  overflow: hidden;
  border-radius: 14px;
  background: var(--white);
  color: var(--ink);
  box-shadow: 0 24px 60px rgba(1 18 31 / 0.3);
}
.panel header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px 16px;
  background: linear-gradient(242deg, var(--slate) 0%, var(--navy) 100%);
  color: var(--white);
}
.panel h2 { margin: 2px 0 0; font: 600 24px/1.05 var(--display); text-transform: uppercase; letter-spacing: 0.01em; }
.eyebrow { margin: 0; font: 600 11px/1.3 var(--text); letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.8; }
.icon-btn { width: 32px; height: 32px; border: 0; border-radius: 50%; background: rgba(255 255 255 / 0.14); color: var(--white); font: 600 20px/1 var(--text); cursor: pointer; }
.icon-btn:hover { background: rgba(255 255 255 / 0.26); }

.body { display: flex; flex: 1; flex-direction: column; min-height: 0; padding: 18px 20px; overflow-y: auto; font-size: 14px; line-height: 1.55; }
.muted { margin: 0 0 12px; color: var(--body); }
.center { text-align: center; margin-top: 40px; }

.form { display: grid; gap: 10px; }
.field { display: grid; gap: 4px; }
.field span { font: 600 11px/1.3 var(--text); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink); }
.field input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(1 18 31 / 0.2);
  border-radius: 6px;
  font: 15px/1.4 var(--text);
  color: var(--ink);
  background: var(--white);
}
.field input:focus { outline: 2px solid var(--slate); outline-offset: 0; border-color: var(--slate); }
.hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.error { min-height: 1em; margin: 0; color: #b3261e; font-size: 13px; }

.btn {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 12px 16px;
  border: 1px solid var(--slate);
  border-radius: 6px;
  background: var(--white);
  color: var(--slate);
  font: 600 15px/1.2 var(--text);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}
.btn.primary { background: var(--slate); color: var(--white); }
.btn.primary:hover { background: var(--slate-dark); }
.btn:disabled { opacity: 0.6; cursor: default; }
.link { padding: 0; border: 0; background: none; color: var(--body); font: 13px var(--text); text-decoration: underline; cursor: pointer; }

.offline .lead { margin: 4px 0 8px; font: 600 22px/1.15 var(--display); text-transform: uppercase; color: var(--ink); }
.offline p { margin: 0 0 12px; color: var(--body); }

.status { margin: 0 0 10px; padding: 10px 12px; border-radius: 8px; background: var(--sand); color: var(--ink); font-size: 13px; }
.dots { display: inline-block; width: 8px; height: 8px; margin-right: 8px; border-radius: 50%; background: var(--slate); animation: pulse 1s infinite ease-in-out; vertical-align: middle; }
@keyframes pulse { 50% { opacity: 0.25; transform: scale(0.7); } }

.log { display: flex; flex: 1; flex-direction: column; gap: 8px; min-height: 120px; overflow-y: auto; padding: 4px 2px; }
.msg { max-width: 85%; padding: 8px 12px; border-radius: 12px; }
.msg p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.msg .who { display: block; margin-bottom: 2px; font-size: 11px; font-weight: 600; opacity: 0.7; }
.msg.visitor { align-self: flex-end; background: var(--slate); color: var(--white); border-bottom-right-radius: 4px; }
.msg.agent { align-self: flex-start; background: var(--mist); color: var(--ink); border-bottom-left-radius: 4px; }
.msg.system { align-self: center; max-width: 100%; padding: 2px 0; color: var(--body); font-size: 12px; font-style: italic; }

.composer { display: flex; gap: 8px; margin-top: 10px; }
.composer textarea {
  flex: 1;
  resize: none;
  padding: 10px 12px;
  border: 1px solid rgba(1 18 31 / 0.2);
  border-radius: 6px;
  font: 14px/1.4 var(--text);
  color: var(--ink);
}
.composer textarea:focus { outline: 2px solid var(--slate); border-color: var(--slate); }
.composer .send { width: auto; margin: 0; }
.foot { margin-top: 8px; text-align: right; }
.typing { margin: 6px 0 0; color: var(--body); font-size: 12px; font-style: italic; }
.fine { margin: 2px 0 0; color: var(--body); font-size: 12px; text-align: center; }
.fine a { color: var(--slate); }
.ended { margin-top: 12px; }

@media (max-width: 480px) {
  .panel { right: 8px; bottom: 80px; width: calc(100vw - 16px); height: calc(100vh - 100px); }
  .launcher { right: 12px; bottom: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .launcher, .dots { transition: none; animation: none; }
}
